/**
 * CourtroomAccessGuard
 * --------------------
 * Bug fix 2026-05-24: chặn student thuộc đội KHÔNG advanced_to_semifinal (top 4
 * sau Vòng Sơ loại) truy cập `/courtroom` — kể cả khi gõ URL trực tiếp.
 *
 * Phase 5 (2026-05-25): mở thêm cho `advanced_to_final` (đội thắng Bán kết
 * có courtroom Chung kết) — fix bug "kẹt ở Bán kết" khi student vào tab
 * Phiên toà ảo sau khi Match họ thắng đã publish.
 *
 * Logic:
 *  - role != student → pass thẳng (admin/secretary/judge có quyền điều phối/chấm)
 *  - student có team `competition_status` ∈ {advanced_to_semifinal,
 *    advanced_to_final} → pass
 *  - còn lại (chưa duyệt, eliminated, chưa publish ranking) → render Access
 *    Denied page với nút quay về Trang chủ.
 *
 * Backend `can_user_join` đã enforce cùng rule (status 403); guard này thêm 1
 * lớp ở FE để student không thấy UI courtroom rỗng/lỗi mà không hiểu lý do.
 * Backend Phase 2/3 thêm match-scoped check (student.team ∈ match.team_ids)
 * — guard FE chỉ kiểm stage, match-scope reject sẽ trả 403 từ join token.
 */
import { Link as RouterLink } from 'react-router-dom'
import { Box, Chip, Container, Paper, Stack, Typography, Button, Alert } from '@mui/material'
import { Block, Home } from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { useGetTeamsQuery } from '../store/api/teamsApi'

const STATUS_LABEL: Record<string, string> = {
  active: 'Đã đăng ký',
  r1_shortlisted_for_review: 'Trong shortlist top 15 (chờ BTC chốt top 4)',
  advanced_to_semifinal: 'Đã vào Bán kết',
  advanced_to_final: 'Đã vào Chung kết',
  eliminated: 'Đã loại',
}

interface Props {
  children: React.ReactNode
}

const COURTROOM_ALLOWED_STATUSES = new Set([
  'advanced_to_semifinal',
  'advanced_to_final',
])

export default function CourtroomAccessGuard({ children }: Props) {
  const { user } = useAppSelector((s) => s.auth)
  const isStudent = user?.role === 'student'
  // Skip API call cho non-student để tiết kiệm round-trip.
  const { data: teams = [], isLoading } = useGetTeamsQuery(undefined, { skip: !isStudent })

  if (!isStudent) return <>{children}</>

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography color="text.secondary">Đang kiểm tra quyền truy cập...</Typography>
      </Container>
    )
  }

  const team = teams.find((t) => t.members?.includes(user!.id))
  const compStatus = (team as any)?.competition_status
  if (compStatus && COURTROOM_ALLOWED_STATUSES.has(compStatus)) return <>{children}</>

  // Diagnostic 2026-05-25: hiển thị status thực tế của team để BTC sửa nhanh
  // khi student báo "đã công bố nhưng không vào VC được" (thường do
  // publish_round_results không set kịp competition_status, hoặc BTC chọn sai
  // advancing_team_ids → team không có trong top 4).
  let reason =
    'Phòng xử ảo chỉ mở cho các đội đã vượt qua Vòng Sơ loại (top 4 vào Vòng Bán kết) hoặc Vòng Chung kết.'
  if (!team) reason = 'Bạn chưa thuộc đội thi nào — không thể truy cập Phòng xử ảo.'
  else if (compStatus === 'eliminated')
    reason = 'Đội của bạn đã bị loại — không đủ điều kiện vào Phòng xử ảo Bán kết / Chung kết.'
  else if (compStatus === 'r1_shortlisted_for_review')
    reason =
      'Đội của bạn đang trong shortlist top 15 — BTC chưa chốt top 4 vào Bán kết. ' +
      'Phòng xử ảo sẽ mở ngay sau khi BTC bấm "Công bố kết quả" cho Vòng Sơ loại.'
  else if (!compStatus)
    reason =
      'Kết quả Vòng Sơ loại chưa được công bố. Phòng xử ảo sẽ mở khi đội bạn được xác nhận lọt vào top 4 Vòng Bán kết.'

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Paper
        elevation={0}
        sx={(t) => ({
          p: { xs: 3, md: 5 },
          textAlign: 'center',
          borderRadius: 3,
          border: `1px solid ${t.palette.divider}`,
        })}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box
            sx={(t) => ({
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: t.palette.error.light,
              color: t.palette.error.contrastText,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            })}
          >
            <Block sx={{ fontSize: 40 }} />
          </Box>
        </Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Không có quyền truy cập Phòng xử ảo
        </Typography>
        <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
          {reason}
        </Alert>
        {team && (
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              variant="outlined"
              label={`Đội: ${team.name}`}
            />
            <Chip
              size="small"
              color={
                compStatus === 'advanced_to_semifinal' || compStatus === 'advanced_to_final'
                  ? 'success'
                  : compStatus === 'eliminated'
                  ? 'error'
                  : 'warning'
              }
              label={`Trạng thái: ${STATUS_LABEL[compStatus as string] || compStatus || '— Chưa có —'}`}
            />
          </Stack>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Nếu bạn cho rằng đây là nhầm lẫn, vui lòng liên hệ Ban tổ chức gửi
          ảnh chụp trang này để BTC kiểm tra/cập nhật trạng thái đội.
        </Typography>
        <Button
          component={RouterLink}
          to="/dashboard"
          variant="contained"
          startIcon={<Home />}
          sx={{ mt: 3, textTransform: 'none' }}
        >
          Về Trang chủ
        </Button>
      </Paper>
    </Container>
  )
}
